/* Серверный режим экрана «Слайды с преимуществами» (infog) на движке шаблона (план вехи 3, lane D; mcard-infog §2A, collage-slides §2.3).
   createInfog(createInfogServer) (features/infog/engine.ts) отдаёт сюда крючки экрана, а каждая вставка SV спрашивает этот объект:
   цена, готовность, фото, создание, хвост и итог берутся только из сервиса заказа кадра (OrderService привязки EngineFrame).
   Правила:
   - одна привязка — один кадр ServerStudio (ключ кадра и его сервис заказа): у другого кадра — и у нового сервиса под прежним ключом —
     своё состояние экрана (S), свой мост загрузок и свой хвост BotRun, поэтому фото, согласие и заказ одного приложения или владельца
     не видны и не уходят под другим;
   - движок не пишет в sessionStorage и не зовёт создание сам: заявку, ключ, отправку, опрос и отказы ведёт сервис (run-driver.ts);
   - S → входы — одна точка записи (inputs-mirror; зеркало зовёт syncFoot движка): те же ключи и кодирование, что у родной формы
     (infog-inputs.ts, SlideOrderForm.tsx); входы, изменённые не зеркалом (повтор из «Истории», заявка родной формы), — в S (viewOf);
   - фото товара — лоток моста загрузок (media-bridge: слоты формы photo, photo_2…, согласие первым, одна очередь, «Продолжить загрузку»
     тем же id, окончательный отказ убирает файл); ключи слотов пишет мост, S.files — только его записи;
   - STY не меняется (его читает «Карточка для маркетплейса»: BotInfog.styles и cover): лента и лист видят опубликованные оформления
     через styles(STY); MP и NS сужаются на месте до опубликованных значений;
   - цена на кнопке — сервиса (studioExpectedPrice: цена количества слайдов); скидка «дешевле» — только если форма её публикует и она
     посчитана для этой же цены (D11, Q-CS7); тексты — словарь профиля в момент показа (uiText, uiFormat). */
import { endpoints } from '../../api';
import type { SpeechOrder } from '../../api/speech';
import { accountLocale, uiFormat, uiText } from '../../i18n';
import { circleBalance, forgetCircleBalance, rememberCircleBalance, toolPrice } from '../studio/circle/tool-helpers';
import { cNum, tailCurrent, tailSteps } from '../studio/circle/order-tail-model';
import { studioAppUnit, studioCheapOffer, type StudioApp, type StudioForm } from '../studio/studio-api';
import { openStudioTarget } from '../studio/studio-navigation';
import { esc, failText, receipt, serverFoot, type BotRunApi, type BotRunRun } from '../studio/engine-kit/bot-run-server';
import { openConsentSheet, type ConsentSheet } from '../studio/engine-kit/consent-sheet';
import { ENGINES, type EngineBinding, type EngineBound, type EngineScreen } from '../studio/engine-kit/engine-host';
import { createInputsMirror, sameInputs, type InputsMirror } from '../studio/engine-kit/inputs-mirror';
import { createMediaBridge, mediaPolicy, type MediaBridge, type MediaItem, type MediaTray } from '../studio/engine-kit/media-bridge';
import { uploadFailureText, type Intent, type OrderService } from '../studio/engine-kit/order-service';
import { archive, archiveUrl, createResultGallery, type GalleryItem, type GalleryLimits, type ResultGallery } from '../studio/engine-kit/result-gallery';
import { createRunDriver, peekIntent, type RunDriver } from '../studio/engine-kit/run-driver';
import { poorBalance, serverUi, type ServerUi } from '../studio/engine-kit/server-ui';
import { chatClick, chatFoot, doneP, openSettings } from '../studio/engine-kit/tail-done';
import { INFOG_STYLES, type SpecRow } from '../slides/product-model';
import { EXTRACT_EMPTY, extractEmpty, extractFailure, extractProduct } from '../slides/product-extract';
import { createSlideEdit, type SlideEdit } from '../slides/slide-edit';
import { productDefaults, slideContractSupported } from '../slides/slide-model';
import { productResult } from '../slides/use-product-result';
import { infogFields, inputsOf, viewOf, viewOfInputs, type InfogView } from './infog-inputs';

/* ---------- контракт с движком (features/infog/engine.ts, serve({...})) ---------- */

/** Фото лотка в виде шаблона; id — запись моста загрузок */
export interface InfogFile {
  name: string;
  size: number;
  url: string;
  w: number;
  h: number;
  id?: string;
  demo?: boolean;
  from?: boolean;
  /** Вырезанный товар (Q-MI9, план вехи 3 §6: PNG или WebP с прозрачными углами): на слайдах — как показательный товар шаблона
      (без y-own), в лотке — светлый фон ig-dm */
  cut?: boolean;
}
/** Что нашли в описании («Заполнить из описания»): преимущества и характеристики с отметками; cut — что не взяли */
export interface InfogFound {
  adv: { t: string; on: boolean }[];
  specs: { k: string; v: string; on: boolean }[];
  cut: { t: string; w: string }[];
}
/** Состояние экрана шаблона (S), см. engine.ts §2 */
export interface InfogState {
  sty: string;
  mp: string;
  mpVia: boolean;
  n: number;
  files: InfogFile[];
  text: string;
  name: string;
  adv: string[];
  specs: string[][];
  spOpen: boolean;
  clean: boolean;
  cheap: boolean;
  menu: unknown;
  sh: { view: 'grid' | 'card' | 'desc' | 'found'; card?: string; from?: string } | null;
  desc: string;
  found: InfogFound | null;
  ret: boolean;
}
/** Оформление шаблона (STY) */
export interface InfogStyle {
  id: string;
  n: string;
  d: string;
  th: string[];
  pop: number;
  isNew: boolean;
  b: string;
  dk: boolean;
  num: boolean;
}
/** Площадка шаблона (MP) */
export interface InfogPlace {
  id: string;
  n: string;
  s?: string;
  ar: string;
  px: string;
  clean?: boolean;
}
/** Слайд шаблонизатора (buildSlides); lbl — подпись слайда сервера */
export type InfogSlide = Record<string, unknown> & { k: string; lbl?: string };
/** Настройки страницы (BotCore.uiDefaults и init каркаса): то, что читает SRV */
export interface EngineUi {
  buzz(kind?: string): void;
  tip(el: Element | null, text: string): void;
  [key: string]: unknown;
}
/** Рамка загрузки BotCore.upload экрана (UP) */
interface EngineUpload {
  o: { mb?: number; multi?: number; [key: string]: unknown };
  say(text: string): void;
  paint(): boolean;
}
/** Подвал BotCore.foot экрана (FOOT) */
interface EngineFoot {
  poor(): boolean;
  poorOpen(): void;
  sync(): void;
}
/** Карточка «Ещё настройки» (BotCore.settingsCard) */
interface EngineSettings {
  sync(): void;
  close(): void;
  readonly isOpen?: boolean;
}
/** Крючки, которые движок отдаёт serve (engine.ts, после OWN_URL): tail, up, foot, sc и rail — геттеры */
export interface InfogEngineHooks {
  S: InfogState;
  CAM: { st: 'idle' | 'asking' | 'on'; req: unknown; stream: unknown; sim: boolean };
  /** Оформления шаблона: не меняются (их читает «Карточка для маркетплейса») */
  STY: readonly InfogStyle[];
  /** Площадки и количества слайдов шаблона: сужаются на месте до опубликованных */
  MP: InfogPlace[];
  NS: number[];
  readonly tail: BotRunApi;
  readonly up: EngineUpload;
  readonly foot: EngineFoot;
  readonly sc: EngineSettings;
  readonly rail: { paint(): void; sync(): void; close(): void };
  root(): HTMLElement | null;
  mounted(): boolean;
  ui(): EngineUi;
  ic(name: string): string;
  esc(value: string): string;
  textParse(value?: string): void;
  textOf(): string;
  buildSlides(c: { name: string; adv: string[]; specs: string[][]; n: number; clean: boolean }): InfogSlide[];
  slideHTML(slide: InfogSlide, o: Record<string, unknown>): string;
  styleOf(id: string): InfogStyle;
  mpOf(id: string): InfogPlace;
  lblOf(slide: InfogSlide | null | undefined): string;
  cleanOn(): boolean;
  content(n?: number): { name: string; adv: string[]; specs: string[][]; n: number; clean: boolean };
  imgFor(): { src: string; own?: boolean; ph?: boolean };
  /** Заготовка «ваш товар» без фото (PLACE шаблона) */
  place(): { src: string; ph: boolean };
  swCheck(list: string[], mp: string): { words: string[]; groups: string[] };
  swNote(res: { words: string[]; groups: string[] }, mp: string, lead?: string): string;
  slideTexts(slide: InfogSlide): string[];
  preselect(found: InfogFound): InfogFound;
  setup(o: unknown): void;
  setLimits(o: { files?: number; mb?: number; tmax?: number }): void;
  setFiles(list: InfogFile[]): void;
  paintPage(): void;
  paintUp(): void;
  paintBox(): void;
  paintSpecs(): void;
  formSync(): void;
  syncSw(): void;
  railCovers(): void;
  renderRes(): void;
  thumbIn(smooth?: boolean): void;
  rswSync(): void;
  resMount(r: BotRunRun): void;
  sheetView(view: string, card?: string): void;
  sheetClose(): void;
  menuClose(): void;
  camStop(): void;
  liveStop(): void;
  runSteps(): string[][];
}
type Missing = '' | 'load' | 'off' | 'blocked' | 'unavailable';
type Ready = '' | 'upload';

export interface InfogServer {
  ui<U extends object>(base: U): ServerUi<U>;
  entry(o: unknown): Record<string, unknown>;
  open(o: unknown): void;
  close(): void;
  tail(create: (o: Record<string, unknown>) => BotRunApi, opts: Record<string, unknown>): BotRunApi;
  wrap<T>(slot: 'cheap', value: T): T;
  /** Опубликованные оформления копиями на языке профиля (STY не меняется) */
  styles(list: readonly InfogStyle[]): readonly InfogStyle[];
  /** Оформление по id копией на языке профиля (слайды оформления в листе) */
  style(id: string): InfogStyle;
  /** Текстовый шов ядра (L-TX): строки BotCore словарём профиля */
  tx(template: string, ...values: unknown[]): string;
  missing(): Missing | null;
  ready(): Ready;
  missText(kind: string): string;
  label(): string | null;
  price(cheap: boolean): number;
  unit(): string;
  nUnit(n: number): string;
  perSlide(): string;
  commit(): void;
  upOpts(): { accept: string; tx(template: string, ...values: unknown[]): string; onPick(files: File[], at?: number): void };
  pick(files: File[]): void;
  remove(i: number): void;
  camText(): string;
  find(btn: HTMLElement): void;
  start(btn: HTMLElement): void;
  snap(): Record<string, unknown>;
  rsw(r: BotRunRun): string;
  slidePic(r: BotRunRun, i: number, current: boolean): string;
  click(d: DOMStringMap, el: HTMLElement, r: BotRunRun | null): boolean;
  leave(go: () => void): void;
  save(btn: HTMLElement): void;
  steps(): string[][];
  drive(r: BotRunRun): void;
  failText(r: BotRunRun): string | null;
  receipt(r: BotRunRun): string;
  /** «Назад» внутри экрана: листы и правка слайда закрываются первыми; true — шаг сделан */
  back(): boolean;
}

/* ---------- общие части ---------- */

interface Core {
  html: { unit(n: number, rest?: string): string };
  prefs: { platform: { get(): string } };
}
const core = () => window.BotCore as unknown as Core;
const MB = 1024 * 1024;
/** «Занята» кнопка шаблона (SteosUI.busy): крутилка, пока идёт действие; уже занята — действие не начинается */
function busyButton<T>(btn: HTMLElement | null, fn: () => Promise<T>): Promise<T | undefined> {
  const busy = window.SteosUI?.busy;
  return btn && busy ? busy(btn, fn) : fn();
}
/** accept рамки BotCore (её проверка типа — до моста): любые фото и расширения, у которых браузер не называет тип;
    точные типы и предел — у моста по политике слота формы */
const ACCEPT = 'image/*,.jpg,.jpeg,.png,.webp,.heic,.heif';
const TRANSPORT = uploadFailureText(null);
const FORM_SAY = [
  'Некоторые файлы повтора больше недоступны. Загрузите их заново.',
  'Этот инструмент сейчас недоступен. Попробуйте обновить форму.',
  'Инструмент обновляется. Обновите форму позже.',
];
/** Умолчания выборов: те же, что у родной формы (SlideOrderForm.tsx useProductDefaults) */
const DEFAULTS = [
  ['platform', ['wb']],
  ['slides', ['3']],
  ['style', []],
  ['cover', ['false', 'text']],
] as const;
/** Слов в склонении: 1 слайд, 3 слайда, 5 слайдов */
function plural(value: number, forms: readonly [string, string, string]) {
  const n = Math.abs(value) % 100,
    m = n % 10;
  return forms[n > 10 && n < 20 ? 2 : m === 1 ? 0 : m >= 2 && m <= 4 ? 1 : 2];
}
const SLIDES: readonly [string, string, string] = ['слайд', 'слайда', 'слайдов'];
/** «3 слайда» на языке профиля: число и слово склонения (у каждого слова — свой перевод) */
export const slidesText = (n: number) => `${n} ${uiText(plural(n, SLIDES))}`;

/** Файлы итога: слайды PNG (и JPEG, WebP) по пределам товарных экранов (slides/use-product-result.ts: до 40 файлов, 200 МБ, 120 с) */
const SLIDE_LIMITS: GalleryLimits = { limit: 40, mimes: /^image\/(jpeg|png|webp)$/, maxBytes: 200 * MB, timeoutMs: 120000 };
/** Картинка слайда сервера во всю рамку слайда шаблона (.igs) */
const IMG_STYLE = 'position:absolute;inset:0;width:100%;height:100%;object-fit:cover;display:block';
/** Тексты страницы заказа (T шаблона, engine.ts TAIL): failText берёт заголовки отказа и сбоя отсюда */
const FAIL = { modH: 'Фото или текст не прошли проверку', techH: 'Не получилось собрать слайды', notMade: 'слайды не созданы' };
const EDIT_ID = 'igEd';
/** Название и преимущества из поля — как textParse шаблона (первая строка, затем непустые строки без маркеров) */
function parseText(text: string) {
  const lines = String(text).replace(/\r/g, '').split('\n'),
    clean = (line: string) => line.replace(/^[\s•·*\-–—]+/, '').trim();
  return { name: clean(lines[0] ?? ''), adv: lines.slice(1).map(clean).filter(Boolean) };
}

/** Состояние одной привязки (кадра ServerStudio) */
interface Frame {
  b: EngineBinding;
  key: string;
  svc: OrderService;
  app: StudioApp;
  bridge: MediaBridge;
  mirror: InputsMirror;
  /** Лоток фото по слотам формы (photo, photo_2…) — после первой сверки формы */
  tray: MediaTray | null;
  /** Форма, для которой прошла первая сверка (пределы, площадки, количества) */
  form: StudioForm | null;
  /** Раскладка формы на экране: опубликованные площадки, количества и оформления */
  layout: string;
  /** Набор id загрузок повтора, превью которого уже читали (не читать тот же набор по кругу) */
  restored: string;
  /** Ошибка формы, уже сказанная в рамке */
  said: string;
  consent: ConsentSheet | null;
  /** «Найти преимущества» идёт */
  extract: AbortController | null;
  /** Форма была на экране при прошлой сверке: новый показ формы спрашивает остаток (как монтирование родной кнопки) */
  formShown: boolean;
  balanceAsk: boolean;
  orderSeen: number | undefined;
  /** Первая сверка формы (или ошибка формы): готовность первой отрисовки (EngineBound.ready) */
  settled: () => void;
  first: Promise<void>;
  driver: RunDriver;
  /** Подсмотренная при открытии заявка (до проверки владельца сервисом) */
  peek: Intent | null;
  /** Слайды итога (один набор на id:revision показанного итога) */
  gallery: ResultGallery;
  /** Правка текста слайда (slide-edit): одна на исходный готовый заказ */
  edit: { root: number; core: SlideEdit } | null;
  /** Лист правки слайда: какой слайд (номер на итоге) */
  editing: number | null;
  /** S сброшен для этого кадра: входы сервиса переносятся в S при ближайшей сверке */
  fresh: boolean;
}

/* ---------- вырезанный товар (Q-MI9) ---------- */

/** Фото товара без фона: PNG или WebP, у которого все четыре угла прозрачны (план вехи 3 §6, Q-MI9 «transparent PNG = cut-out»).
    Решается один раз на файл; без createImageBitmap/OffscreenCanvas (старый WebView) и при любой ошибке — не вырезан (своё фото) */
const CUT_TYPE = /^image\/(png|webp)$/i;
const canCut = () => typeof createImageBitmap === 'function' && typeof OffscreenCanvas === 'function';
export async function cutOut(file: Blob | null | undefined): Promise<boolean> {
  if (!file || !CUT_TYPE.test(file.type) || !canCut()) return false;
  try {
    const bmp = await createImageBitmap(file);
    const N = 32;
    const c = new OffscreenCanvas(N, N);
    const g = c.getContext('2d');
    if (!g) {
      bmp.close();
      return false;
    }
    g.drawImage(bmp, 0, 0, N, N);
    bmp.close();
    const a = (x: number, y: number) => g.getImageData(x, y, 1, 1).data[3];
    return [a(0, 0), a(N - 1, 0), a(0, N - 1), a(N - 1, N - 1)].every(v => v < 8);
  } catch {
    return false;
  }
}

/* ---------- сервер экрана ---------- */

let instance: { bind(b: EngineBinding): EngineBound } | null = null;

export function createInfogServer(h: InfogEngineHooks): InfogServer {
  const { S } = h;
  let frame: Frame | null = null;
  let bound: Frame | null = null;
  /** Кадр, к которому относятся S и экземпляр хвоста BotRun (объект, а не ключ: ключи кадров повторяются после нового входа в раздел) */
  let painted: Frame | null = null;
  let tailOpts: { create: (o: Record<string, unknown>) => BotRunApi; opts: Record<string, unknown> } | null = null;
  let inst: BotRunApi | null = null;
  const facade = {} as BotRunApi;
  /** Шаблонные значения S на новый кадр (engine.ts §2) */
  const fresh = { sty: S.sty, n: S.n };
  /** Опубликованные оформления по форме и языку (копии STY на языке профиля) */
  let styled: { form: StudioForm | null; locale: string; list: readonly InfogStyle[] } = { form: null, locale: '', list: h.STY };
  /** Оформление на языке профиля: название, описание, категории и метка — словарём (данные шаблона, STY не меняется) */
  const said = (item: InfogStyle): InfogStyle => ({ ...item, n: uiText(item.n), d: uiText(item.d), th: item.th.map(theme => uiText(theme)), b: uiText(item.b) });

  const cur = () => (bound && bound === frame ? frame : null);
  const live = (f: Frame) => {
    const s = f.svc.get();
    return { s, d: f.svc.derive(s) };
  };
  const onForm = () => h.mounted() && !h.tail.run;
  /** Подсказка-стекло у элемента; элемента нет на месте — у кнопки подвала */
  const tip = (el: Element | null, text: string) => {
    const at = el?.isConnected ? el : (h.root()?.querySelector('.ap-foot .btn') ?? null);
    if (text && at) h.ui().tip(at, uiText(text));
  };
  const slotKeys = (form: StudioForm | null | undefined) => infogFields(form).photos.map(field => field.key);
  /** Заявка заказа: сервиса, а пока он не проверил владельца — подсмотренная при открытии */
  const intentOf = (f: Frame | null): Intent | null => {
    const s = f?.svc.get();
    if (!f || !s) return null;
    return s.intent ?? (!s.identity && !s.integrityError ? f.peek : null);
  };
  /** Итог на экране: подтверждённая правка слайда (подзаказ) или заказ сервиса */
  const shownOrder = (f: Frame | null): SpeechOrder | null => {
    const order = f?.svc.get().order ?? null;
    if (!f || order?.status !== 'done') return order;
    return f.edit?.root === order.id ? (f.edit.core.shown() ?? order) : order;
  };
  /** Вид S для входов (cleanOn — с закреплённой обложкой Ozon Fresh) */
  const sView = (): InfogView => ({ text: S.text, sty: S.sty, mp: S.mp, n: S.n, clean: h.cleanOn(), specs: S.specs as SpecRow[] });

  /* ---- фото лотка ---- */
  /** Вырезан ли товар на фото записи (по адресу blob записи: решается один раз); undefined — ещё читается */
  const cuts = new Map<string, boolean>();
  const cutting = new Set<string>();
  function cutOf(f: Frame, item: MediaItem): boolean | undefined {
    if (cuts.has(item.url)) return cuts.get(item.url);
    if (cutting.has(item.url)) return undefined;
    const file = item.file;
    if (!file || !CUT_TYPE.test(file.type) || !canCut()) {
      cuts.set(item.url, false);
      return false;
    }
    cutting.add(item.url);
    void cutOut(file).then(cut => {
      cuts.set(item.url, cut);
      cutting.delete(item.url);
      if (f.tray?.list().some(it => it.url === item.url)) trayChanged(f);
    });
    return undefined;
  }
  /** S.files — записи лотка моста, у которых есть что показать (превью повтора, пока читается, не показывается; PNG и WebP — пока
      не решено, вырезан ли товар: так обложки и слайды не перерисовываются второй раз) */
  function filesOf(f: Frame): InfogFile[] {
    return (f.tray?.list() ?? [])
      .filter(item => !!item.url && cutOf(f, item) !== undefined)
      .map(item => ({ name: item.name, size: item.size, url: item.url, w: item.meta.width ?? 1200, h: item.meta.height ?? 1200, id: item.id, cut: !!cuts.get(item.url) }));
  }
  /** Лоток моста изменился: S.files заново, рамка и всё, что зависит от фото (обложки ленты, кнопка) */
  function trayChanged(f: Frame) {
    if (cur() !== f) return;
    /* PNG выбора ждёт проверки вырезки (cutOf), JPEG — нет: без ожидания JPEG вставали в лоток раньше PNG, а PNG потом «новым»
       в начало — trayPaint шаблона докручивал ленту в конец. Фото одного выбора встают разом, когда вырезка известна у всех
       (её конец снова зовёт trayChanged) */
    if ((f.tray?.list() ?? []).some(item => !!item.url && cutOf(f, item) === undefined)) return;
    h.setFiles(filesOf(f));
    if (!onForm()) return;
    h.paintUp();
    h.formSync();
  }
  /** Фото одного выбора приходят в лоток по одному (put моста на каждый файл): рамка перерисовывается один раз на выбор, как у шаблона,
      где выбранные фото встают разом. Иначе trayPaint шаблона видит каждое следующее фото «новым» и докручивает ленту в конец (и подпись лотка остаётся «на одно фото»: «Главное фото пойдёт на обложку» вместо «…, остальные в слайды») */
  const putting = new WeakSet<Frame>();
  function putSoon(f: Frame) {
    if (putting.has(f)) return;
    putting.add(f);
    queueMicrotask(() => {
      putting.delete(f);
      trayChanged(f);
    });
  }
  /** Права на фото не подтверждены, а фото повтора стоят в лотке: кнопка подтверждает их (лист согласия без файла) */
  const rightsNeeded = (f: Frame) => !f.svc.get().rights && !!f.tray?.list().some(item => item.state === 'ready');
  function confirmRights(f: Frame) {
    f.consent?.close();
    f.consent = openConsentSheet({
      rights: () => f.svc.get().rights,
      locked: () => f.svc.derive().locked,
      setRights: on => f.svc.setters.rights(on),
      confirm: true,
      texts: { row: 'Права на фото товара', sub: 'Подтверждаю право использовать фото товара' },
      onContinue: () => {
        f.consent = null;
        if (cur() === f) h.foot.sync();
      },
      onClose: () => {
        f.consent = null;
      },
    });
  }

  /* ---- готовность ---- */
  function missing(): Missing | null {
    const f = cur();
    if (!f) return 'load';
    const { s, d } = live(f);
    if (s.intent && !d.orderId) return '';
    if (!s.identity && !s.integrityError) return 'load';
    if (s.integrityError || !s.identity || s.identity.appId !== f.app.id) return 'blocked';
    /* форма не загрузилась или устарела: кнопка «Обновить форму» (recovery) */
    if (!s.form) return s.error ? '' : 'load';
    if (s.versionStale) return '';
    if (!slideContractSupported(s.form, 'infog')) return 'off';
    if (!s.form.available) return 'unavailable';
    /* чинить, а не создавать: «Продолжить загрузку», «Подтвердите права на фото» — кнопка активна */
    if (recovery(f)) return '';
    return null;
  }
  function ready(): Ready {
    const f = cur();
    if (!f) return 'upload';
    /* фото ещё грузятся или читаются превью повтора */
    return f.bridge.pending() ? 'upload' : '';
  }
  /** Что починить вместо создания: форма не загрузилась или устарела — перечитать; загрузка не подтверждена — продолжить тем же id;
      фото повтора без подтверждённых прав — подтвердить */
  function recovery(f: Frame): ((btn: HTMLElement) => void) | null {
    const { s } = live(f);
    if ((!s.form && s.error && !s.integrityError) || s.versionStale) return () => f.svc.setters.revision(value => value + 1);
    if (f.bridge.stalled() && !s.busy && s.error === TRANSPORT) return btn => void busyButton(btn, () => f.bridge.resume());
    if (s.form && rightsNeeded(f)) return () => confirmRights(f);
    return null;
  }
  function label(): string | null {
    const f = cur();
    if (!f) return null;
    const { s, d } = live(f);
    if (s.intent && !d.orderId) return uiText('Продолжить проверку');
    if ((!s.form && s.error && !s.integrityError) || s.versionStale) return uiText('Обновить форму');
    if (f.bridge.stalled() && !s.busy && s.error === TRANSPORT) return uiText('Продолжить загрузку');
    if (s.form && rightsNeeded(f)) return uiText('Подтвердите права на фото');
    if (s.form?.chargeable === false) return uiFormat('Создать {0}', slidesText(S.n));
    return null;
  }
  /** Скидка «дешевле» формы — только для той цены, для которой она посчитана (цена формы, D11, Q-CS7) */
  function cheapOffer(f: Frame | null) {
    if (!f) return null;
    const { s, d } = live(f);
    const offer = studioCheapOffer(s.form);
    return offer && s.form && d.price === s.form.expected_price ? offer : null;
  }

  /** Цена слайда: платная форма — прайс сервера, иначе карточка каталога */
  const slidePrice = () => {
    const f = cur() ?? frame;
    return studioAppUnit(toolPrice(f?.app.price).amount, f?.svc.get().form, 'variant');
  };
  /* ---- сверка формы с сервисом (каждый зафиксированный вид) ---- */
  /** Площадки и количества шаблона — только опубликованные (порядок шаблона); поля нет — как в шаблоне */
  function narrow(form: StudioForm) {
    const f = infogFields(form);
    if (f.platform) {
      const keep = h.MP.filter(item => f.platform!.values.includes(item.id));
      if (keep.length) h.MP.splice(0, h.MP.length, ...keep);
    }
    if (f.slides) {
      const keep = h.NS.filter(n => f.slides!.values.includes(String(n)));
      if (keep.length) h.NS.splice(0, h.NS.length, ...keep);
    }
  }
  /** Входы → S (повтор, «Изменить», заявка родной формы, значения по умолчанию) */
  function applyView(view: InfogView) {
    S.text = view.text;
    h.textParse(view.text);
    if (view.sty) S.sty = view.sty;
    if (view.mp && h.MP.some(item => item.id === view.mp)) {
      S.mp = view.mp;
      S.mpVia = false;
    }
    if (view.n !== undefined && h.NS.includes(view.n)) S.n = view.n;
    if (view.clean !== undefined) S.clean = view.clean;
    S.specs = view.specs.map(row => [row[0], row[1]]);
  }
  /** Фото повтора: id слотов во входах, которых нет в лотке, — превью (один раз на набор); записи лотка, которых больше нет во входах
      (сервис снял загрузку), уходят */
  function photosSync(f: Frame) {
    const tray = f.tray;
    if (!tray) return;
    const { s, d } = live(f);
    const ids = tray.keys().map(key => s.inputs[key]).filter((id): id is string => !!id);
    const list = tray.list();
    const known = (id: string) => list.some(item => item.uploadId === id || item.pendingId === id);
    if (ids.length && !ids.every(known) && f.restored !== ids.join()) {
      f.restored = ids.join();
      void tray.restore(ids).finally(() => {
        if (cur() === f && h.mounted()) changed(f);
      });
      return;
    }
    if (d.locked || f.bridge.pending()) return;
    for (const item of list) if (item.state === 'ready' && item.uploadId && !ids.includes(item.uploadId)) tray.remove(item.id);
  }
  /** Раскладка формы на экране: опубликованные площадки, количества и оформления (без формы — пусто) */
  const layoutOf = (f: Frame) =>
    f.svc.get().form ? JSON.stringify([h.MP.map(item => item.id), h.NS, server.styles(h.STY).map(item => item.id)]) : '';
  /** Сверка без отрисовки: первая форма (пределы, площадки, количества, лоток), значения по умолчанию, входы → S.
      → входы изменили S (форму надо нарисовать заново) */
  function prime(f: Frame): boolean {
    const { s, d } = live(f);
    const form = s.form;
    if ((form || s.error || s.integrityError) && f.settled) f.settled();
    if (form && f.form !== form) {
      /* первая сверка этой формы: пределы лотка и поля, площадки и количества */
      f.form = form;
      const fields = infogFields(form);
      const keys = slotKeys(form);
      const policy = keys[0] ? mediaPolicy(form, keys[0]) : null;
      h.setLimits({ files: keys.length, mb: policy ? policy.maxBytes / MB : 0, tmax: fields.text?.max_length ?? 0 });
      narrow(form);
      f.tray = keys.length ? f.bridge.slots(keys) : null;
    }
    /* значения по умолчанию — только в опубликованные выборы и не поверх заявки (use-product-defaults.ts) */
    if (form && !d.locked && !s.intent) {
      const add = productDefaults(form, s.inputs, DEFAULTS);
      if (add.length)
        f.svc.setters.inputs(previous => {
          const next = { ...previous };
          for (const [key, value] of add) if (!next[key]) next[key] = value;
          return next;
        });
    }
    let repaint = false;
    const now = f.svc.get();
    if (form && (f.fresh || f.mirror.external(now.inputs))) {
      /* S только что сброшен (другой кадр рисовал экран) или входы изменило не зеркало: в S — если они не те, что S дал бы сам
         (запись моста о фото S не трогает) */
      if (f.fresh || !sameInputs(inputsOf(form, sView(), now.inputs), now.inputs)) {
        applyView(viewOf(form, now.inputs));
        repaint = true;
      }
      f.fresh = false;
      f.mirror.seen(now.inputs);
    }
    return repaint;
  }
  /** Форма на экран: сверка без отрисовки, затем одна отрисовка (её раскладка запоминается) */
  function paintForm(f: Frame) {
    prime(f);
    f.layout = layoutOf(f);
    h.paintPage();
  }
  function formSync(f: Frame) {
    const repaint = prime(f);
    const now = f.svc.get();
    if (now.form && onForm()) photosSync(f);
    const layout = layoutOf(f);
    if (layout !== f.layout || repaint) {
      f.layout = layout;
      if (onForm()) h.paintPage();
    }
    if (!onForm()) return;
    /* ошибка формы (не загрузилась, файлы повтора недоступны) — один раз в рамке */
    const error = now.integrityError ? '' : now.error;
    if (error !== f.said) {
      f.said = error;
      if (FORM_SAY.includes(error)) h.up.say(uiText(error));
    }
    h.foot.sync();
  }
  /** S.files — те же записи, что в лотке (мост убрал или переставил запись без своего отклика) */
  function filesSync(f: Frame) {
    const now = filesOf(f);
    if (now.length !== S.files.length || now.some((file, i) => file.id !== S.files[i]?.id || file.url !== S.files[i]?.url || !!file.cut !== !!S.files[i]?.cut))
      trayChanged(f);
  }
  function changed(f: Frame) {
    if (cur() !== f || !h.mounted()) return;
    filesSync(f);
    formSync(f);
    f.bridge.sync();
    f.driver.sync();
    f.consent?.refresh();
    balance(f);
  }
  /** Остаток для «Не хватает Circle» — тот же, что у родной кнопки (GET /app/balance, память на минуту): спрашивается при каждом
      показе формы, если в памяти его нет; созданный или открытый заказ делает память устаревшей */
  function balance(f: Frame) {
    const orderId = f.svc.derive().orderId;
    if (orderId && orderId !== f.orderSeen) {
      f.orderSeen = orderId;
      forgetCircleBalance();
    }
    const shown = onForm(),
      first = shown && !f.formShown;
    f.formShown = shown;
    if (!first || f.balanceAsk || circleBalance() !== null || f.svc.get().form?.chargeable === false) return;
    f.balanceAsk = true;
    const seen = f.orderSeen;
    void endpoints
      .balance()
      .then(
        value => {
          if (f.orderSeen === seen) rememberCircleBalance(value?.circle);
        },
        () => {
          /* ошибка — проверки нет, решает сервер (402), как у родной кнопки */
        },
      )
      .finally(() => {
        f.balanceAsk = false;
        if (cur() === f && onForm()) h.foot.sync();
      });
  }

  /* ---- хвост BotRun: фасад одного экземпляра на кадр ---- */
  function rebuild() {
    if (!tailOpts) return;
    const { create, opts } = tailOpts;
    const base = typeof opts.missing === 'function' ? (opts.missing as () => string) : () => '';
    /* тексты страницы заказа шаблона (T) — словарём профиля в момент показа (BotRun берёт их через val); подпись итога — по доставке */
    const given = (opts.T as Record<string, unknown>) ?? {};
    const T = Object.fromEntries(Object.entries(given).map(([key, value]) => [key, typeof value === 'string' ? () => uiText(value) : value]));
    const doneText = typeof given.doneP === 'string' ? given.doneP : '';
    const head = (opts.head as Record<string, unknown>) ?? {};
    const foot = { ...((opts.foot as Record<string, unknown>) ?? {}) };
    /* подписи подвала шаблона (mod: «Изменить текст») — функции: BotRun берёт их через val при каждом показе */
    for (const [key, value] of Object.entries(foot))
      if (Array.isArray(value) && typeof value[2] === 'string') foot[key] = [value[0], value[1], () => uiText(value[2] as string)];
    /* действия итога и «Что дальше» — подписи словарём (BotRun вставляет их разметкой) */
    const labelled = (list: unknown) =>
      Array.isArray(list) ? list.map(item => (Array.isArray(item) && typeof item[2] === 'string' ? [item[0], item[1], esc(uiText(item[2]))] : item)) : list;
    const acts = opts.acts as ((r: BotRunRun) => unknown) | undefined,
      next = opts.next as ((r: BotRunRun) => unknown) | undefined;
    inst = create({
      ...opts,
      T: { ...T, doneP: () => doneP(shownOrder(cur() ?? frame), esc(uiText(doneText)), 'ig') },
      head: { ...head, title: () => uiText(String(head.title ?? '')) },
      ...(acts ? { acts: (r: BotRunRun) => labelled(acts(r)) } : {}),
      ...(next ? { next: (r: BotRunRun) => labelled(next(r)) } : {}),
      card: (r: BotRunRun) => card(r),
      drive: (r: BotRunRun) => server.drive(r),
      failText: (r: BotRunRun) => server.failText(r),
      receipt: (r: BotRunRun) => server.receipt(r),
      foot: chatFoot(serverFoot(foot, 'ig'), 'ig'),
      steps: () => server.steps(),
      snap: () => server.snap(),
      price: () => cur()?.svc.derive().price ?? 0,
      /* повтор заявки и «Попробовать ещё раз» решает сервис: форма в памяти им не нужна */
      missing: () => {
        const f = cur();
        if (f) {
          const { s, d } = live(f);
          if (s.intent || d.orderId) return '';
        }
        return base();
      },
    });
    for (const key of Object.keys(inst)) {
      if (key === 'run' || Object.hasOwn(facade, key)) continue;
      (facade as unknown as Record<string, unknown>)[key] = (...args: unknown[]) =>
        (inst as unknown as Record<string, (...a: unknown[]) => unknown>)[key](...args);
    }
  }
  Object.defineProperty(facade, 'run', { enumerable: true, get: () => inst?.run ?? null });

  /* ---- привязка кадра ---- */
  function bind(b: EngineBinding): EngineBound {
    if (!frame || frame.key !== b.key || frame.svc !== b.svc) {
      if (frame) {
        frame.bridge.cancel();
        frame.consent?.close();
        frame.extract?.abort();
        editClose();
        frame.gallery.release();
        frame.edit?.core.dispose();
      }
      frame = makeFrame(b);
    } else frame.b = b;
    const mine = frame;
    bound = mine;
    return {
      changed: () => changed(mine),
      release() {
        if (bound === mine) bound = null;
      },
      ready: () => mine.first,
      back: () => cur() === mine && server.back(),
    };
  }
  function makeFrame(b: EngineBinding): Frame {
    const f = {} as Frame;
    const guard =
      <A extends unknown[]>(fn: (...args: A) => void) =>
      (...args: A) => {
        if (frame === f) fn(...args);
      };
    let settle!: () => void;
    const first = new Promise<void>(resolve => (settle = resolve));
    Object.assign(f, {
      b,
      key: b.key,
      svc: b.svc,
      app: b.app,
      tray: null,
      form: null,
      layout: '',
      restored: '',
      said: '',
      consent: null,
      extract: null,
      formShown: false,
      balanceAsk: false,
      orderSeen: undefined,
      first,
      settled: () => {
        settle();
        f.settled = undefined as unknown as () => void;
      },
    });
    f.mirror = createInputsMirror(b.svc);
    f.peek = null;
    f.edit = null;
    f.editing = null;
    f.fresh = true;
    f.gallery = createResultGallery(SLIDE_LIMITS);
    f.driver = createRunDriver({
      svc: b.svc,
      tail: () => h.tail,
      root: () => h.root(),
      mounted: () => cur() === f && h.mounted(),
      localSteps: () => server.steps(),
      paintForm: () => (cur() === f ? paintForm(f) : h.paintPage()),
      tip: (el, text) => tip(el, text),
      onDone: () => onDone(f),
    });
    f.bridge = createMediaBridge({
      svc: b.svc,
      owner: () => f.svc.get().identity?.owner,
      say: guard((text: string) => h.up.say(text)),
      put: guard(() => putSoon(f)),
      drop: guard(() => trayChanged(f)),
      update: guard(() => trayChanged(f)),
      consentWith: () => ({ texts: { row: 'Права на фото товара', sub: 'Подтверждаю право использовать фото товара' } }),
    });
    return f;
  }

  const server: InfogServer = {
    ui: base =>
      serverUi(base, {
        poorBalance: () => {
          const f = cur();
          /* кнопка чинит («Обновить форму», «Продолжить загрузку»), а не создаёт: «Не хватает · Пополнить» её не заслоняет */
          if (!f || recovery(f)) return Infinity;
          const s = f.svc.get();
          return poorBalance({ balance: circleBalance(), form: s.form, intent: s.intent }, true);
        },
      }),
    /* вход экрана — входы сервиса (повтор, «Изменить»): в setup шаблона ничего не идёт, S ставит сверка формы */
    entry: () => ({}),
    open(o) {
      const f = cur();
      if (!f) {
        /* без привязки (кадр не активен) — форма без действий: missing() держит «Проверяем доступность» */
        h.setup(o);
        h.paintPage();
        return;
      }
      if (painted !== f) {
        /* другой кадр или новый сервис под прежним ключом: фото, текст, выборы, скидка и хвост прежнего сюда не переходят */
        h.liveStop();
        h.sheetClose();
        Object.assign(S, {
          sty: fresh.sty,
          n: fresh.n,
          files: [],
          text: '',
          name: '',
          adv: [],
          specs: [],
          clean: false,
          cheap: false,
          sh: null,
          desc: '',
          found: null,
          mpVia: false,
          mp: core().prefs.platform.get(),
        });
        rebuild();
        painted = f;
        f.layout = '';
        f.form = null;
        f.fresh = true;
      }
      /* открытие на форме — новый показ кнопки: остаток спрашивается, если его нет в памяти */
      f.formShown = false;
      h.setup(o);
      h.setFiles(filesOf(f));
      const root = h.root();
      if (h.tail.run && root) {
        h.tail.show(root);
        changed(f);
        return;
      }
      f.peek = peekIntent(f.app.id);
      /* форма рисуется один раз (paintForm водителя): сверка перерисует её, только если раскладка или входы станут другими */
      if (f.driver.first(f.peek) === 'form' && root) root.scrollTop = 0;
      changed(f);
    },
    close() {
      const f = frame;
      if (!f) return;
      f.consent?.close();
      f.consent = null;
      f.extract?.abort();
      f.extract = null;
      editClose();
    },
    tail(create, opts) {
      tailOpts = { create, opts };
      rebuild();
      return facade;
    },
    wrap<T>(_slot: 'cheap', value: T): T {
      const cheap = value as unknown as { where?: unknown; go?: unknown };
      /* скидку «Сделать дешевле» публикует форма, и она посчитана для текущей цены: без неё у подвала нет ни цены, ни действия скидки;
         цена со скидкой — цена формы (не BotCore.cheapPrice) */
      return {
        /* где водяной знак (лист «Не хватает Circle») — словарём профиля в момент показа */
        where: () => uiText(String(cheap.where ?? '')),
        get price() {
          return cheapOffer(cur()) ? () => server.price(true) : undefined;
        },
        get go() {
          return cheapOffer(cur()) ? cheap.go : undefined;
        },
        get percent() {
          return cheapOffer(cur())?.percent;
        },
      } as unknown as T;
    },
    styles(list) {
      const form = cur()?.svc.get().form ?? null;
      const locale = accountLocale();
      if (styled.form === form && styled.locale === locale && styled.list !== h.STY) return styled.list;
      const field = infogFields(form).style;
      const known = field ? list.filter(item => field.values.includes(item.id)) : list.slice();
      /* опубликованные оформления, которых шаблон не знает, — в конец ленты подписью словаря экранов (вид — первого оформления) */
      const extra = (field?.values ?? [])
        .filter(id => !list.some(item => item.id === id))
        .map(id => ({ ...list[0], id, n: INFOG_STYLES[id]?.name ?? id, d: INFOG_STYLES[id]?.sub ?? '', th: INFOG_STYLES[id]?.themes ?? [], pop: 0, isNew: false, b: '' }));
      styled = { form, locale, list: [...known, ...extra].map(said) };
      return styled.list;
    },
    style(id) {
      return server.styles(h.STY).find(item => item.id === id) ?? said(h.styleOf(id));
    },
    tx: (template, ...values) => uiFormat(template, ...values),
    missing,
    ready,
    missText(kind) {
      const f = cur();
      const s = f?.svc.get();
      switch (kind) {
        case 'load':
          return uiText('Проверяем доступность');
        case 'blocked':
          return uiText(s?.integrityError || 'Проверяем доступность');
        case 'off':
          return uiText('Этот вариант пока недоступен');
        case 'unavailable':
          return uiText('Создание сейчас недоступно');
        case 'upload':
          return uiText('Загружаем фото');
        default:
          return '';
      }
    },
    label,
    price(cheap) {
      const f = cur();
      if (!f) return 0;
      const offer = cheap ? cheapOffer(f) : null;
      return offer ? offer.price : f.svc.derive().price;
    },
    unit() {
      const amount = slidePrice();
      return amount !== null && amount > 0 ? core().html.unit(amount, uiText('за слайд')) : '';
    },
    nUnit(n) {
      const field = infogFields(cur()?.svc.get().form).slides;
      const price = field?.prices?.[String(n)];
      return typeof price === 'number' && Number.isSafeInteger(price) && price >= 0 ? core().html.unit(price) : '';
    },
    perSlide() {
      const amount = slidePrice();
      return amount !== null ? cNum(amount) : '—';
    },
    commit() {
      const f = cur();
      const form = f?.svc.get().form;
      if (!f || !form) return;
      f.mirror.commit(previous => inputsOf(form, sView(), previous));
    },
    upOpts: () => ({
      accept: ACCEPT,
      /* свои строки рамки BotCore.upload («Загрузить», «Снять», «Можно до N фото», «N из M») — словарём профиля */
      tx: (template: string, ...values: unknown[]) => uiFormat(template, ...values),
      onPick(files, at) {
        const f = cur();
        if (!f || !files.length) return;
        if (!f.tray) {
          h.up.say(uiText('Проверяем доступность'));
          return;
        }
        void f.tray.pickSlots(files, at ?? undefined);
      },
    }),
    pick(files) {
      server.upOpts().onPick(files);
    },
    remove(i) {
      const f = cur();
      const id = S.files[i]?.id;
      if (!f?.tray || !id) return;
      f.tray.remove(id);
      trayChanged(f);
    },
    camText: () => uiText('Камера недоступна. Загрузите файл.'),
    find(btn) {
      const f = cur();
      if (!f || f.extract) return;
      const abort = new AbortController();
      f.extract = abort;
      void busyButton(btn, () => extractProduct(S.desc, 'infog', abort.signal))
        .then(found => {
          if (abort.signal.aborted || cur() !== f || !found) return;
          if (extractEmpty(found)) {
            tip(btn, EXTRACT_EMPTY);
            return;
          }
          if (!S.sh || S.sh.view !== 'desc') return;
          S.found = h.preselect({
            adv: found.advantages.map(t => ({ t, on: false })),
            specs: found.specs.map(([k, v]) => ({ k, v, on: false })),
            cut: [],
          });
          h.sheetView('found');
        })
        .catch(failure => {
          if (!abort.signal.aborted && cur() === f) tip(btn, extractFailure(failure));
        })
        .finally(() => {
          if (f.extract === abort) f.extract = null;
        });
    },
    start(btn) {
      const f = cur();
      if (!f) return;
      const { s, d } = live(f);
      if (s.intent && !d.orderId) {
        h.tail.start();
        return;
      }
      const fix = recovery(f);
      if (fix) {
        fix(btn);
        return;
      }
      if (h.foot.poor()) {
        h.foot.poorOpen();
        return;
      }
      h.tail.start();
    },
    /* снимок входа заказа (r.snap шаблона): из тела заявки (подсмотренной), без неё — из S; фото — только если в лотке то же фото заказа */
    snap() {
      const f = cur();
      const s = f?.svc.get();
      const it = intentOf(f);
      const view = it ? viewOfInputs(s?.form, it.body.inputs as Record<string, unknown>) : sView();
      const mp = view.mp && h.MP.some(item => item.id === view.mp) ? view.mp : S.mp;
      const { name, adv } = parseText(view.text);
      const specs = view.specs.map(row => [row[0].trim(), row[1].trim()]).filter(row => row[0] && row[1]);
      const clean = !!view.clean || !!h.mpOf(mp).clean;
      const n = view.n ?? S.n;
      const main = f?.tray?.list()[0];
      const photoKey = slotKeys(s?.form)[0] ?? 'photo';
      const own = !!main?.url && (!it || (!!main.uploadId && it.body.inputs[photoKey] === main.uploadId));
      return {
        sty: view.sty ?? S.sty,
        mp,
        ar: h.mpOf(mp).ar,
        cheap: !!(it ? it.body.cheap : S.cheap),
        name,
        adv,
        specs,
        clean,
        img: own ? { src: main!.url, own: !cuts.get(main!.url) } : h.place(),
        ex: [],
        file: null,
        slides: h.buildSlides({ name, adv, specs, n, clean }),
      };
    },
    /* стоп-слова по текстам слайдов сервера (title, body, text) и площадке заказа */
    rsw(r) {
      const order = shownOrder(cur() ?? frame);
      const texts = productResult(order).slides.flatMap(slide => [slide.title, slide.body, slide.text]).filter(Boolean);
      const mp = String(r.snap.mp ?? S.mp);
      return h.swNote(h.swCheck(texts, mp), mp, uiText('Перед скачиванием уберите'));
    },
    /* слайд итога — PNG сервера в рамке слайда шаблона; пока файл грузится — пустая рамка оформления */
    slidePic(r, i) {
      const f = cur() ?? frame;
      const item = f ? f.gallery.items(shownOrder(f))[i] : undefined;
      const cls = `igs ig-${esc(String(r.snap.sty ?? ''))}${r.snap.ar === '1:1' ? ' y-sq' : ''}`;
      return `<div class="${cls}">${item ? `<img src="${esc(item.url)}" alt="" draggable="false" style="${IMG_STYLE}">` : ''}</div>`;
    },
    click(d, el, r) {
      const f = cur();
      if (d.igRetry !== undefined || d.igRchk !== undefined || d.igFix !== undefined) {
        if (!f || f.driver.busy()) return true;
        if (d.igRetry !== undefined) h.tail.retry();
        else if (d.igRchk !== undefined) void f.driver.check(el);
        else void f.driver.leave(() => h.tail.exit(() => h.paintPage()), false, '');
        return true;
      }
      if (d.igChat !== undefined) {
        chatClick({ order: f?.svc.get().order, el, text: 'Тут откроется тема чата, куда бот прислал слайды', tip: (at, text) => tip(at, text) });
        return true;
      }
      if (d.igSettings !== undefined) {
        openSettings();
        return true;
      }
      if (!f || !r) return false;
      /* «Ещё вариант» — к форме с теми же настройками, без заказа (D10, как родной итог) */
      if (d.igVar !== undefined) {
        if (r.st === 'done') server.leave(() => h.tail.exit(() => h.paintPage()));
        return true;
      }
      const inputs = (intentOf(f)?.body.inputs ?? f.svc.get().inputs) as Record<string, unknown>;
      const value = (key: string | undefined) => (key && typeof inputs[key] === 'string' ? (inputs[key] as string) : undefined);
      const fields = infogFields(f.svc.get().form);
      const platform = value(fields.platform?.key ?? 'platform');
      /* «Что дальше»: свои экраны с теми же входами, «назад» вернёт сюда (родной итог: ProductOrderResult) */
      if (d.igSeo !== undefined) {
        const text = value(fields.text?.key ?? fields.name?.key ?? 'text');
        openStudioTarget({ screen: 'text', tool: 'seo', ret: true, options: { ...(text?.split('\n')[0] ? { text } : {}), ...(platform ? { platform } : {}) } });
        return true;
      }
      if (d.igVcov !== undefined) {
        if (r.st !== 'done') return true;
        const photo = value(fields.photos[0]?.key ?? 'photo');
        openStudioTarget({ screen: 'video', preset: 'cover', ret: true, options: photo ? { product_photo: photo } : {} });
        return true;
      }
      /* центральный слайд итога — правка его текста (лист: заголовок и текст, PATCH без новой генерации) */
      if (el.matches('#igRi .pc.cur') && r.st === 'done') {
        editOpen(f, Number(el.dataset.i), el);
        return true;
      }
      return false;
    },
    leave(go) {
      const f = cur();
      if (!f) return;
      void f.driver.leave(go, false, '');
    },
    save(btn) {
      const f = cur();
      const order = shownOrder(f);
      if (!f || order?.status !== 'done') return;
      const items = f.gallery.items(order);
      const saveBtn = (window.BotCore as unknown as { saveBtn?(btn: HTMLElement, make: () => Promise<{ data: Blob; name: string }>, o: object): void }).saveBtn;
      if (!items.length || !saveBtn) {
        tip(btn, 'Слайды ещё загружаются');
        return;
      }
      const ui = h.ui();
      /* несколько слайдов — архив итога (owner-protected путь заказа), один — сам файл */
      saveBtn(
        btn,
        async () =>
          items.length > 1 && archiveUrl(order)
            ? { data: await archive(order), name: 'steos-infographic.zip' }
            : { data: items[0].blob, name: items[0].name || 'steos-infographic.png' },
        { buzz: () => ui.buzz(), tip: (el: Element, text: string) => ui.tip(el, text) },
      );
    },
    steps: () => h.runSteps().map(([key, icon, run, done]) => [key, icon, uiText(run), uiText(done)]),
    drive(r) {
      const f = cur();
      if (f) void f.driver.drive(r);
    },
    failText(r) {
      const f = cur() ?? frame;
      const s = f?.svc.get();
      const order = s?.order ?? null;
      const steps = tailSteps(order);
      return failText(r, {
        order,
        error: s ? s.integrityError || s.error : '',
        texts: FAIL,
        step: steps.length ? steps[tailCurrent(order, steps)] : undefined,
      });
    },
    receipt(r) {
      const f = cur() ?? frame;
      const s = f?.svc.get();
      const cheap = s?.intent ? !!s.intent.body.cheap : !!r.snap.cheap;
      return receipt(r, s?.order ?? null, { cheap, where: 'на слайдах' });
    },
    back() {
      const f = cur();
      if (editSheet()?.isOpen) {
        editClose();
        return true;
      }
      if (f?.consent?.isOpen()) {
        f.consent.close();
        return true;
      }
      const sh = S.sh;
      if (sh) {
        if (sh.view === 'card' && sh.from === 'grid') h.sheetView('grid');
        else if (sh.view === 'found') h.sheetView('desc');
        else h.sheetClose();
        return true;
      }
      if (h.sc.isOpen) {
        h.sc.close();
        return true;
      }
      return false;
    },
  };

  /** Карточка заказа итога (runWhat шаблона): оформление, площадка, слайдов — на языке профиля; название товара; стоп-слова итога */
  function card(r: BotRunRun): string {
    const snap = r.snap as { sty?: string; mp?: string; name?: string; slides?: unknown[] };
    const what = [
      uiFormat('Оформление «{0}»', uiText(h.styleOf(String(snap.sty ?? '')).n)),
      uiText(h.mpOf(String(snap.mp ?? '')).n),
      slidesText(snap.slides?.length ?? 0),
    ].join(' · ');
    const q = (facade as unknown as { q?(meta: string, text: string): string }).q;
    return (q ? q(what, String(snap.name ?? '')) : '') + `<div class="ig-rsw" id="igRsw">${server.rsw(r)}</div>`;
  }

  /* ---- итог: слайды сервера ---- */
  /** Правка слайда на исходный готовый заказ (одна на заказ; цепочка — та же, что у родного итога) */
  function editOf(f: Frame, order: SpeechOrder): SlideEdit | null {
    const owner = f.svc.get().identity?.owner;
    if (!owner || order.status !== 'done') return null;
    if (f.edit?.root !== order.id) {
      f.edit?.core.dispose();
      const core = createSlideEdit({
        owner,
        rootId: order.id,
        root: () => (f.svc.get().order?.id === order.id ? f.svc.get().order : order),
        onChange: () => {
          if (cur() === f) onDone(f);
          editPaint();
        },
      });
      f.edit = { root: order.id, core };
      void core.load();
    }
    return f.edit.core;
  }
  /** Заказ готов (каждая сверка): правка и слайды итога; пришли — слайды встают на место */
  function onDone(f: Frame) {
    const order = f.svc.get().order;
    if (!order || order.status !== 'done') return;
    editOf(f, order);
    void f.gallery.load(shownOrder(f)).then(items => {
      if (items && cur() === f) resultPaint(items);
    });
  }
  /** Слайды итога по файлам сервера: подписи шаблона, если число слайдов совпало со снимком, иначе «Обложка», «Слайд N» */
  function resultPaint(items: GalleryItem[]) {
    const r = h.tail.run;
    if (!r || r.st !== 'done' || !h.mounted()) return;
    const slides = (r.snap.slides as Array<Record<string, unknown>> | undefined) ?? [];
    if (slides.length !== items.length) {
      r.snap.slides = items.map((_, i) => ({ k: i ? 'life' : 'cover', v: i, t: '', lbl: i ? uiFormat('Слайд {0}', i + 1) : uiText('Обложка') }));
      const run = r as BotRunRun & { cur?: number };
      if (typeof run.cur === 'number' && run.cur >= items.length) run.cur = 0;
      h.tail.refresh();
      return;
    }
    h.root()
      ?.querySelectorAll<HTMLElement>('#igTh [data-ig-go]')
      .forEach(button => {
        const html = server.slidePic(r, Number(button.dataset.igGo), false);
        if (button.innerHTML !== html) button.innerHTML = html;
      });
    h.renderRes();
    h.rswSync();
  }

  /* ---- лист правки слайда (Q-MI1 A: заголовок и текст, PATCH без новой генерации) ---- */
  interface CoreSheet {
    html(): string;
    open(html: string): void;
    paint(html: string): void;
    close(): void;
    readonly isOpen: boolean;
  }
  interface SheetCore {
    sheet(o: { id: string; label: string; onClose(): void }): CoreSheet;
    shead(o: Record<string, unknown>): string;
    html: { box(o: Record<string, unknown>): string };
    boxSync(o: Record<string, unknown>): void;
    offBtn(label: string, miss: string, cls?: string): string;
  }
  let sheetMade: { bc: unknown; sheet: CoreSheet } | null = null;
  const editSheet = (): CoreSheet | null => (sheetMade && sheetMade.bc === window.BotCore ? sheetMade.sheet : null);
  let draft: { title: string; body: string; slide: number } | null = null;
  function sheetOf(): CoreSheet {
    const bc = window.BotCore as unknown as SheetCore;
    let made = editSheet();
    if (!made) {
      made = bc.sheet({
        id: EDIT_ID,
        label: uiText('Изменить текст'),
        onClose: () => {
          draft = null;
          if (frame) frame.editing = null;
        },
      });
      sheetMade = { bc, sheet: made };
    }
    if (!document.getElementById(EDIT_ID)) (document.getElementById('bot') ?? document.body).insertAdjacentHTML('beforeend', made.html());
    return made;
  }
  function editHTML(): string {
    const bc = window.BotCore as unknown as SheetCore;
    const f = cur() ?? frame;
    const edit = f?.edit?.core.view();
    const at = f?.editing ?? 0;
    const pending = edit?.chain?.pending ?? null;
    const box = (id: string, value: string, max: number, label: string) =>
      bc.html.box({
        id: `${EDIT_ID}${id}`,
        cntId: `${EDIT_ID}${id}C`,
        taId: `${EDIT_ID}${id}T`,
        value,
        max,
        label: h.esc(uiText(label)),
        aria: uiText(label),
        full: false,
        chips: '',
      });
    const empty = !draft || (!draft.title.trim() && !draft.body.trim());
    const over = !!draft && (draft.title.length > 90 || draft.body.length > 240);
    const cta = edit?.busy
      ? bc.offBtn(uiText('Сохраняем…'), uiText('Сохраняем…'), 'fin')
      : pending && !pending.child
        ? `<button class="btn fin" type="button" data-ig-eresume>${h.esc(uiText('Продолжить проверку'))}</button>`
        : pending
          ? bc.offBtn(uiText('Сохранить'), uiText('Перерисовываем слайд…'), 'fin')
          : empty || over
            ? bc.offBtn(uiText('Сохранить'), uiText(over ? 'Текст не помещается на слайд. Сократите заголовок или текст.' : 'Напишите заголовок или текст'), 'fin')
            : `<button class="btn fin" type="button" data-ig-esave>${h.esc(uiText('Сохранить'))}</button>`;
    return (
      `<div class="cbody ig-eb" id="${EDIT_ID}Sb">${bc.shead({ title: uiText('Изменить текст'), sub: at ? uiFormat('Слайд {0}', at + 1) : uiText('Обложка'), x: 'data-bsheet-x' })}` +
      box('Ti', draft?.title ?? '', 90, 'Заголовок') +
      box('Bo', draft?.body ?? '', 240, 'Текст') +
      `<p class="bc-cap"><span>${h.esc(uiText('Перерисуем слайд тем же шаблоном, без новой генерации.'))}</span></p>` +
      (edit?.error ? `<p class="bc-cap bad" role="alert"><span>${h.esc(uiText(edit.error))}</span></p>` : '') +
      `<div class="cta" id="${EDIT_ID}Cta">${cta}</div></div>`
    );
  }
  /** Лист правки на месте: кнопка и строка ошибки по состоянию правки (поля не трогаются — в них пишут) */
  function editPaint() {
    const sheet = editSheet();
    if (!sheet?.isOpen) return;
    const tmp = document.createElement('div');
    tmp.innerHTML = editHTML();
    const cta = document.getElementById(`${EDIT_ID}Cta`),
      next = tmp.querySelector(`#${EDIT_ID}Cta`);
    if (cta && next && cta.innerHTML !== next.innerHTML) cta.innerHTML = next.innerHTML;
    const alert = document.querySelector(`#${EDIT_ID}Sb .bc-cap.bad`),
      want = tmp.querySelector('.bc-cap.bad');
    if (want && !alert) document.getElementById(`${EDIT_ID}Cta`)?.insertAdjacentElement('beforebegin', want);
    else if (alert && !want) alert.remove();
    else if (alert && want && alert.textContent !== want.textContent) alert.replaceWith(want);
  }
  /** Слайд i итога: текст слайда сервера (title, body; без них — text), лист правки */
  function editOpen(f: Frame, i: number, el: HTMLElement) {
    const order = shownOrder(f);
    const item = f.gallery.items(order)[i];
    const slide = item ? productResult(order).slides.find(entry => entry.id === item.index) : undefined;
    const root = f.svc.get().order;
    const edit = root ? editOf(f, root) : null;
    const view = edit?.view();
    if (!slide || !edit || !view || view.blocked || !view.chain) {
      tip(el, 'Этот слайд нельзя изменить');
      return;
    }
    draft = { title: slide.title || (slide.body ? '' : slide.text), body: slide.body, slide: slide.id };
    f.editing = i;
    sheetOf().open(editHTML());
    h.ui().buzz();
  }
  function editClose() {
    const sheet = editSheet();
    if (sheet?.isOpen) sheet.close();
    draft = null;
  }
  /* поля и кнопки листа правки (лист — в #bot, вне корня экрана: свои слушатели) */
  document.addEventListener('input', event => {
    const t = event.target as HTMLTextAreaElement | null;
    if (!draft || !t?.id || !t.closest?.(`#${EDIT_ID}`)) return;
    const id = t.id === `${EDIT_ID}TiT` ? 'Ti' : t.id === `${EDIT_ID}BoT` ? 'Bo' : '';
    if (!id) return;
    if (id === 'Ti') draft.title = t.value;
    else draft.body = t.value;
    (window.BotCore as unknown as SheetCore).boxSync({ box: `${EDIT_ID}${id}`, cnt: `${EDIT_ID}${id}C`, len: t.value.trim().length, max: id === 'Ti' ? 90 : 240 });
    editPaint();
  });
  document.addEventListener('click', event => {
    const t = event.target instanceof Element ? event.target.closest<HTMLElement>('[data-ig-esave],[data-ig-eresume]') : null;
    const f = cur();
    const edit = f?.edit?.core;
    if (!t || !t.closest(`#${EDIT_ID}`) || !edit) return;
    if (t.hasAttribute('data-ig-eresume')) {
      void busyButton(t, () => edit.resume()).then(ok => {
        if (ok) editClose();
        else editPaint();
      });
      return;
    }
    if (!draft) return;
    const { slide, title, body } = draft;
    void busyButton(t, () => edit.save(slide, title.trim(), body.trim())).then(ok => {
      if (ok) editClose();
      else editPaint();
    });
  });

  /* ---- смена языка профиля (i18n: событие steos:locale): открытый экран рисуется заново ---- */
  /** Подписи этапов идущего заказа — строки на момент создания хвоста: им — подписи нового языка (ключи этапов те же) */
  function relabel(r: BotRunRun) {
    const next = server.steps();
    for (const step of r.steps) {
      const same = next.find(item => item[0] === step[0]);
      if (same) {
        step[2] = same[2];
        step[3] = same[3];
      }
    }
  }
  document.addEventListener('steos:locale', () => {
    const f = cur();
    const r = h.tail.run;
    if (r) relabel(r);
    if (!f || !h.mounted()) return;
    const root = h.root();
    if (r && root) h.tail.show(root);
    else h.paintPage();
    changed(f);
  });
  /* новый снимок Me (пополнение) уже положил остаток в память кнопки (tool-helpers): подвал формы — по нему */
  document.addEventListener('steos:account-updated', () => {
    if (cur() && onForm()) h.foot.sync();
  });

  instance = { bind };
  return server;
}

/** ENGINES.infog.bind: привязка кадра к серверу экрана, созданному движком (без движка в серверном режиме — пустая привязка) */
export function bindInfogEngine(b: EngineBinding): EngineBound {
  return instance ? instance.bind(b) : { changed() {}, release() {} };
}

/** Экран «infog» реестра движков студии: движок шаблона window.BotInfog, атрибуты data-ig-*, без правой панели, знак заказа «infog» */
export const INFOG_ENGINE: EngineScreen = { global: 'BotInfog', prefix: 'ig', side: false, kind: 'infog', bind: bindInfogEngine };

export function registerInfogEngine() {
  ENGINES.infog = INFOG_ENGINE;
}
